/**
 * Records carry the country_name that the ingest reference data stamps on them,
 * which is not always the name ref_country offers in the dropdown. A group
 * collapses one affiliate into a single option that expands back to every value
 * a record may actually hold.
 */
export const COUNTRY_GROUP_LABELS: Record<string, string> = {
  UruBoPa: "Uruguay, Bolivia & Paraguay",
  CCAV: "Caribbean & Venezuela",
};

/** Record-side country_name values that ref_country does not list. */
export const COUNTRY_NAME_ALIASES: Record<string, string[]> = {
  "Uruguay, Paraguay & Bolivia Countries": ["UruBoPa"],
  "Caribbean & Venezuela Caribbean Countries": [
    "Caribbean & Venezuela Countries",
  ],
  "Islas Turcas y Caicos": ["Turks and Caicos Islands"],
  "San Cristóbal y Nieves": ["Saint Kitts and Nevis"],
  "San Vicente y las Granadinas": ["Saint Vincent and the Grenadines"],
};

export interface CountryFilterOptions {
  /** Option labels available per ts_region. */
  byRegion: Record<string, string[]>;
  /** Option label -> every country value a matching record may carry. */
  members: Record<string, string[]>;
}

export const buildCountryFilterOptions = (
  rows: any[] = []
): CountryFilterOptions => {
  const byRegion: Record<string, Set<string>> = {};
  const members: Record<string, Set<string>> = {};

  rows.forEach((row) => {
    const name = row?.country_name;
    const region = row?.ts_region;
    if (!name || !region) return;

    const label = COUNTRY_GROUP_LABELS[row?.affiliate] || name;

    if (!byRegion[region]) byRegion[region] = new Set();
    byRegion[region].add(label);

    if (!members[label]) members[label] = new Set();
    members[label].add(name);
    (COUNTRY_NAME_ALIASES[name] || []).forEach((alias) =>
      members[label].add(alias)
    );
  });

  const toSorted = (set: Set<string>) =>
    Array.from(set).sort((a, b) => a.localeCompare(b));

  return {
    byRegion: Object.fromEntries(
      Object.entries(byRegion).map(([region, set]) => [region, toSorted(set)])
    ),
    members: Object.fromEntries(
      Object.entries(members).map(([label, set]) => [label, Array.from(set)])
    ),
  };
};

/** Turns selected filter labels into the country values the backend matches on. */
export const expandCountryFilters = (
  selected: string[] = [],
  members: Record<string, string[]> = {}
): string[] =>
  Array.from(new Set(selected.flatMap((label) => members[label] || [label])));
